import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  FormHelperText,
  Alert,
  Divider,
  Grid,
  Chip,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  InputAdornment,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Security as SecurityIcon,
  Storage as StorageIcon,
  Email as EmailIcon,
  Notifications as NotificationsIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Psychology as AIIcon,
  GitHub as GitHubIcon,
  Api as ApiIcon,
  Visibility,
  VisibilityOff,
  CheckCircle,
  Error as ErrorIcon,
  ExpandMore
} from '@mui/icons-material';
import AdminService, { SystemConfig, CacheStats } from '../../services/AdminService';
import SystemSettingsService, { SystemSetting } from '../../services/SystemSettingsService';

interface AIConnectionStatus {
  provider: string;
  configured: boolean;
  connection_status?: string;
  tested_at?: string;
  error?: string;
  url_configured?: boolean;
  api_key_configured?: boolean;
  server_url?: string;
  model_tested?: string;
}

interface AdminSystemSetting extends SystemSetting {
  editableValue: string;
  originalValue: string;
}

/**
 * Enhanced System Settings Component
 * Admin interface for managing system configuration and AI settings
 */
const SystemSettings: React.FC = () => {
  const [config, setConfig] = useState<SystemConfig | null>(null);
  const [cacheStats, setCacheStats] = useState<CacheStats | null>(null);
  const [settings, setSettings] = useState<AdminSystemSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingConnection, setTestingConnection] = useState<string | null>(null);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [clearCacheDialog, setClearCacheDialog] = useState(false);
  const [showPasswords, setShowPasswords] = useState<{[key: string]: boolean}>({});
  const [connectionStatus, setConnectionStatus] = useState<{[key: string]: AIConnectionStatus}>({});

  useEffect(() => {
    loadSystemConfig();
    loadCacheStats();
    loadSystemSettings();
  }, []);

  const loadSystemConfig = async () => {
    try {
      const configData = await AdminService.getSystemConfig();
      setConfig(configData);
    } catch (error) {
      console.error('Failed to load system config:', error);
      setError('Failed to load system configuration');
    } finally {
      setLoading(false);
    }
  };

  const loadCacheStats = async () => {
    try {
      const stats = await AdminService.getCacheStats();
      setCacheStats(stats);
    } catch (error) {
      console.error('Failed to load cache stats:', error);
    }
  };

  const loadSystemSettings = async () => {
    try {
      const settingsData = await SystemSettingsService.getSettings();
      const formatted: AdminSystemSetting[] = (settingsData || []).map(setting => {
        const rawValue = setting.value ?? '';
        const stringValue = typeof rawValue === 'boolean' ? (rawValue ? 'true' : 'false') : rawValue.toString();

        return {
          ...setting,
          editableValue: setting.encrypted ? '' : stringValue,
          originalValue: setting.encrypted ? '' : stringValue
        };
      });
      setSettings(formatted);
    } catch (error) {
      console.error('Failed to load system settings:', error);
    }
  };

  const handleConfigChange = (field: keyof SystemConfig, value: any) => {
    if (config) {
      setConfig({ ...config, [field]: value });
    }
  };

  const handleSettingChange = (key: string, value: any) => {
    setSettings(prev => prev.map(setting => 
      setting.key === key 
        ? { ...setting, editableValue: value?.toString?.() ?? value }
        : setting
    ));
  };

  const handleSaveConfig = async () => {
    if (!config) return;
    
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await AdminService.updateSystemConfig(config);
      setSuccess('System configuration updated successfully');
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to update configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // Save AI and integration settings
      const scopedSettings = settings.filter(s => ['ai', 'speckit', 'integrations'].includes(s.category));

      const settingsToUpdate = scopedSettings
        .filter(setting => {
          if (setting.encrypted) {
            // Only submit encrypted values when user provided new input
            return setting.editableValue !== '';
          }

          return setting.editableValue !== setting.originalValue;
        })
        .map(setting => ({
          key: setting.key,
          value: setting.editableValue,
          type: setting.type,
          encrypted: setting.encrypted,
          is_public: setting.is_public,
          category: setting.category,
          description: setting.description
        }));

      if (settingsToUpdate.length === 0) {
        setSuccess('No changes detected');
        setSaving(false);
        return;
      }

      await SystemSettingsService.updateSettings(settingsToUpdate);
      setSuccess('Settings updated successfully');
      loadSystemSettings(); // Reload to get updated timestamps
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleInitializeAISettings = async () => {
    try {
      setError('');
      const result = await SystemSettingsService.initializeAISettings();
      setSuccess('AI settings initialized successfully');
      loadSystemSettings();
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to initialize AI settings');
    }
  };

  const handleTestAIConnection = async (provider: 'claude' | 'openai' | 'openwebui') => {
    setTestingConnection(provider);
    setError('');
    setSuccess('');

    try {
  const result = await SystemSettingsService.testAIConnection(provider);
      setConnectionStatus(prev => ({ ...prev, [provider]: result }));

      if (result.connection_status === 'success') {
        setSuccess(`${provider.charAt(0).toUpperCase() + provider.slice(1)} connection test successful`);
      } else {
        setError(result.error || `Connection to ${provider} failed. Please verify configuration.`);
      }
    } catch (error: any) {
      setConnectionStatus(prev => ({ 
        ...prev, 
        [provider]: { 
          provider,
          configured: false,
          connection_status: 'failed',
          error: error.message 
        } 
      }));
      setError(error.message || 'Connection test failed');
    } finally {
      setTestingConnection(null);
    }
  };

  const handleClearCache = async () => {
    try {
      await AdminService.clearCache();
      setSuccess('Cache cleared successfully');
      loadCacheStats();
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to clear cache');
    }
    setClearCacheDialog(false);
  };

  const togglePasswordVisibility = (key: string) => {
    setShowPasswords(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const getSettingsByCategory = (category: string): AdminSystemSetting[] => {
    return settings.filter(setting => setting.category === category);
  };

  const renderSettingField = (setting: AdminSystemSetting) => {
    const isPassword = setting.encrypted;
    const showPassword = showPasswords[setting.key];
    const currentValue = setting.editableValue ?? '';

    // Special handling for AI provider dropdown
    if (setting.key === 'ai.default_provider') {
      return (
        <FormControl fullWidth>
          <InputLabel>Default AI Provider</InputLabel>
          <Select
            value={currentValue || 'claude'}
            onChange={(e) => handleSettingChange(setting.key, e.target.value)}
            label="Default AI Provider"
          >
            <MenuItem value="claude">Anthropic Claude</MenuItem>
            <MenuItem value="openai">OpenAI (GPT-4)</MenuItem>
            <MenuItem value="openwebui">Open WebUI (Self-hosted)</MenuItem>
            <MenuItem value="mock">Mock (Testing Only)</MenuItem>
          </Select>
          <FormHelperText>Choose which AI provider to use for the AI Console</FormHelperText>
        </FormControl>
      );
    }

    if (setting.type === 'boolean') {
      return (
        <FormControlLabel
          control={
            <Switch
              checked={currentValue === 'true' || currentValue === '1'}
              onChange={(e) => handleSettingChange(setting.key, e.target.checked ? 'true' : 'false')}
            />
          }
          label={setting.description}
        />
      );
    }

    if (setting.type === 'integer') {
      return (
        <TextField
          fullWidth
          type="number"
          label={setting.description}
          value={currentValue}
          onChange={(e) => handleSettingChange(setting.key, e.target.value)}
          helperText={`Key: ${setting.key}`}
        />
      );
    }

    return (
      <TextField
        fullWidth
        type={isPassword && !showPassword ? 'password' : 'text'}
        label={setting.description}
        value={currentValue}
        placeholder={isPassword && setting.has_value ? 'Stored securely. Enter a new value to replace.' : undefined}
        onChange={(e) => handleSettingChange(setting.key, e.target.value)}
        helperText={`Key: ${setting.key}${isPassword && setting.has_value ? ' • Value stored securely' : ''}`}
        InputProps={isPassword ? {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => togglePasswordVisibility(setting.key)}
                edge="end"
                size="small"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        } : undefined}
      />
    );
  };

  const renderConnectionStatus = (provider: string) => {
    const status = connectionStatus[provider];
    if (!status) return null;

    return (
      <Box display="flex" alignItems="center" gap={1} mt={1}>
        {status.connection_status === 'success' ? (
          <CheckCircle color="success" fontSize="small" />
        ) : (
          <ErrorIcon color="error" fontSize="small" />
        )}
        <Box display="flex" flexDirection="column">
          <Typography variant="caption">
            {status.connection_status === 'success' 
              ? `Connected successfully at ${status.tested_at}`
              : status.error || 'Connection failed'
            }
          </Typography>
          {status.connection_status === 'success' && status.model_tested && (
            <Typography variant="caption" color="text.secondary">
              Model: {status.model_tested}
            </Typography>
          )}
          {provider === 'openwebui' && status.connection_status !== 'success' && (
            <Typography variant="caption" color="text.secondary">
              URL configured: {status.url_configured ? 'Yes' : 'No'} • API key configured: {status.api_key_configured ? 'Yes' : 'No'}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1">
          <SettingsIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
          System Settings
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              loadSystemConfig();
              loadSystemSettings();
              loadCacheStats();
            }}
          >
            Reload
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={activeTab === 0 ? handleSaveConfig : handleSaveSettings}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

      <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
        <Tab label="General Settings" />
        <Tab label="AI & Integrations" />
      </Tabs>

      {activeTab === 0 && config && (
        <Grid container spacing={3}>
          {/* General Settings */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardHeader
                title={
                  <Box display="flex" alignItems="center" gap={1}>
                    <SettingsIcon />
                    <Typography variant="h6">General Settings</Typography>
                  </Box>
                }
              />
              <CardContent>
                <Box display="flex" flexDirection="column" gap={3}>
                  <TextField
                    fullWidth
                    label="Application Name"
                    value={config.app_name}
                    onChange={(e) => handleConfigChange('app_name', e.target.value)}
                  />
                  
                  <TextField
                    fullWidth
                    label="Application URL"
                    value={config.app_url}
                    onChange={(e) => handleConfigChange('app_url', e.target.value)}
                  />

                  <FormControl fullWidth>
                    <InputLabel>Timezone</InputLabel>
                    <Select
                      value={config.app_timezone}
                      onChange={(e) => handleConfigChange('app_timezone', e.target.value)}
                      label="Timezone"
                    >
                      <MenuItem value="UTC">UTC</MenuItem>
                      <MenuItem value="America/New_York">Eastern Time</MenuItem>
                      <MenuItem value="America/Chicago">Central Time</MenuItem>
                      <MenuItem value="America/Denver">Mountain Time</MenuItem>
                      <MenuItem value="America/Los_Angeles">Pacific Time</MenuItem>
                    </Select>
                  </FormControl>

                  <TextField
                    fullWidth
                    label="Max File Upload Size (MB)"
                    type="number"
                    value={config.max_file_upload_size}
                    onChange={(e) => handleConfigChange('max_file_upload_size', parseInt(e.target.value))}
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.maintenance_mode}
                        onChange={(e) => handleConfigChange('maintenance_mode', e.target.checked)}
                      />
                    }
                    label="Maintenance Mode"
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.debug_mode}
                        onChange={(e) => handleConfigChange('debug_mode', e.target.checked)}
                      />
                    }
                    label="Debug Mode"
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Security Settings */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardHeader
                title={
                  <Box display="flex" alignItems="center" gap={1}>
                    <SecurityIcon />
                    <Typography variant="h6">Security Settings</Typography>
                  </Box>
                }
              />
              <CardContent>
                <Box display="flex" flexDirection="column" gap={3}>
                  <TextField
                    fullWidth
                    label="Session Lifetime (minutes)"
                    type="number"
                    value={config.session_lifetime}
                    onChange={(e) => handleConfigChange('session_lifetime', parseInt(e.target.value))}
                  />

                  <TextField
                    fullWidth
                    label="Minimum Password Length"
                    type="number"
                    value={config.password_min_length}
                    onChange={(e) => handleConfigChange('password_min_length', parseInt(e.target.value))}
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.require_password_confirmation}
                        onChange={(e) => handleConfigChange('require_password_confirmation', e.target.checked)}
                      />
                    }
                    label="Require Password Confirmation"
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.enable_user_registration}
                        onChange={(e) => handleConfigChange('enable_user_registration', e.target.checked)}
                      />
                    }
                    label="Enable User Registration"
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.email_verification_required}
                        onChange={(e) => handleConfigChange('email_verification_required', e.target.checked)}
                      />
                    }
                    label="Email Verification Required"
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* System Performance */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardHeader
                title={
                  <Box display="flex" alignItems="center" gap={1}>
                    <StorageIcon />
                    <Typography variant="h6">System Performance</Typography>
                  </Box>
                }
              />
              <CardContent>
                <Box display="flex" flexDirection="column" gap={3}>
                  <FormControl fullWidth>
                    <InputLabel>Cache Driver</InputLabel>
                    <Select
                      value={config.cache_driver}
                      onChange={(e) => handleConfigChange('cache_driver', e.target.value)}
                      label="Cache Driver"
                    >
                      <MenuItem value="redis">Redis</MenuItem>
                      <MenuItem value="file">File</MenuItem>
                      <MenuItem value="database">Database</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel>Queue Driver</InputLabel>
                    <Select
                      value={config.queue_driver}
                      onChange={(e) => handleConfigChange('queue_driver', e.target.value)}
                      label="Queue Driver"
                    >
                      <MenuItem value="redis">Redis</MenuItem>
                      <MenuItem value="database">Database</MenuItem>
                      <MenuItem value="sync">Sync</MenuItem>
                    </Select>
                  </FormControl>

                  <TextField
                    fullWidth
                    label="Backup Retention (days)"
                    type="number"
                    value={config.backup_retention_days}
                    onChange={(e) => handleConfigChange('backup_retention_days', parseInt(e.target.value))}
                  />

                  {cacheStats && (
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>Cache Statistics</Typography>
                      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="body2">Total Keys: {cacheStats.total_keys}</Typography>
                        <Chip label={`Hit Ratio: ${cacheStats.hit_ratio}`} color="primary" size="small" />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Memory Usage: {cacheStats.memory_usage}
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<DeleteIcon />}
                        onClick={() => setClearCacheDialog(true)}
                        sx={{ mt: 1 }}
                      >
                        Clear Cache
                      </Button>
                    </Paper>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Email Settings */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardHeader
                title={
                  <Box display="flex" alignItems="center" gap={1}>
                    <EmailIcon />
                    <Typography variant="h6">Email Settings</Typography>
                  </Box>
                }
              />
              <CardContent>
                <Box display="flex" flexDirection="column" gap={3}>
                  <FormControl fullWidth>
                    <InputLabel>Mail Driver</InputLabel>
                    <Select
                      value={config.mail_driver}
                      onChange={(e) => handleConfigChange('mail_driver', e.target.value)}
                      label="Mail Driver"
                    >
                      <MenuItem value="smtp">SMTP</MenuItem>
                      <MenuItem value="sendmail">Sendmail</MenuItem>
                      <MenuItem value="mail">PHP Mail</MenuItem>
                      <MenuItem value="log">Log (Development)</MenuItem>
                    </Select>
                  </FormControl>

                  <Typography variant="body2" color="text.secondary">
                    Email configuration settings are managed through environment variables for security.
                    Update your .env file to modify SMTP settings.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {activeTab === 1 && (
        <Grid container spacing={3}>
          {/* AI Settings */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardHeader
                title={
                  <Box display="flex" alignItems="center" gap={1}>
                    <AIIcon />
                    <Typography variant="h6">AI Configuration</Typography>
                  </Box>
                }
                action={
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleInitializeAISettings}
                  >
                    Initialize Defaults
                  </Button>
                }
              />
              <CardContent>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="h6">Anthropic Claude Settings</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box display="flex" flexDirection="column" gap={3}>
                      {getSettingsByCategory('ai').filter(s => s.key.includes('claude')).map(setting => (
                        <Box key={setting.key}>
                          {renderSettingField(setting)}
                          {setting.key === 'ai.claude.api_key' && (setting.editableValue || setting.has_value) && (
                            <Box mt={1}>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleTestAIConnection('claude')}
                                disabled={testingConnection === 'claude'}
                                startIcon={testingConnection === 'claude' ? <CircularProgress size={16} /> : <ApiIcon />}
                              >
                                Test Connection
                              </Button>
                              {renderConnectionStatus('claude')}
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>

                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="h6">OpenAI Settings</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box display="flex" flexDirection="column" gap={3}>
                      {getSettingsByCategory('ai').filter(s => s.key.includes('openai')).map(setting => (
                        <Box key={setting.key}>
                          {renderSettingField(setting)}
                          {setting.key === 'ai.openai.api_key' && (setting.editableValue || setting.has_value) && (
                            <Box mt={1}>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleTestAIConnection('openai')}
                                disabled={testingConnection === 'openai'}
                                startIcon={testingConnection === 'openai' ? <CircularProgress size={16} /> : <ApiIcon />}
                              >
                                Test Connection
                              </Button>
                              {renderConnectionStatus('openai')}
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>

                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="h6">Open WebUI Settings (Self-hosted)</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box display="flex" flexDirection="column" gap={3}>
                      {getSettingsByCategory('ai').filter(s => s.key.includes('openwebui')).map(setting => (
                        <Box key={setting.key}>
                          {renderSettingField(setting)}
                          {setting.key === 'ai.openwebui.api_key' && (setting.editableValue || setting.has_value) && (
                            <Box mt={1}>
                              {(() => {
                                const urlSetting = settings.find(s => s.key === 'ai.openwebui.url');
                                const canTest = !!((urlSetting?.editableValue || urlSetting?.has_value) && (setting.editableValue || setting.has_value));
                                return (
                                  <Box display="flex" flexDirection="column" gap={0.5}>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => handleTestAIConnection('openwebui')}
                                      disabled={testingConnection === 'openwebui' || !canTest}
                                      startIcon={testingConnection === 'openwebui' ? <CircularProgress size={16} /> : <ApiIcon />}
                                    >
                                      Test Connection
                                    </Button>
                                    {!canTest && (
                                      <Typography variant="caption" color="text.secondary">
                                        Provide both the server URL and API key to enable testing.
                                      </Typography>
                                    )}
                                  </Box>
                                );
                              })()}
                              {renderConnectionStatus('openwebui')}
                            </Box>
                          )}
                        </Box>
                      ))}
                      <Alert severity="info">
                        <Typography variant="caption">
                          Open WebUI is a self-hosted AI interface. Configure your server URL (e.g., http://localhost:8080) 
                          and optionally an API key if authentication is enabled. Supports various models like LLaMA, Mistral, CodeLLaMA, etc.
                        </Typography>
                      </Alert>
                    </Box>
                  </AccordionDetails>
                </Accordion>

                <Box mt={3}>
                  <Typography variant="h6" gutterBottom>General AI Settings</Typography>
                  <Box display="flex" flexDirection="column" gap={3}>
                    {getSettingsByCategory('ai').filter(s => 
                      !s.key.includes('claude') && 
                      !s.key.includes('openai') && 
                      !s.key.includes('openwebui')
                    ).map(setting => (
                      <Box key={setting.key}>
                        {renderSettingField(setting)}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* SpecKit Settings */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardHeader
                title={
                  <Box display="flex" alignItems="center" gap={1}>
                    <ApiIcon />
                    <Typography variant="h6">SpecKit Configuration</Typography>
                  </Box>
                }
              />
              <CardContent>
                <Box display="flex" flexDirection="column" gap={3}>
                  {getSettingsByCategory('speckit').map(setting => (
                    <Box key={setting.key}>
                      {renderSettingField(setting)}
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Integration Settings */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardHeader
                title={
                  <Box display="flex" alignItems="center" gap={1}>
                    <GitHubIcon />
                    <Typography variant="h6">External Integrations</Typography>
                  </Box>
                }
              />
              <CardContent>
                <Box display="flex" flexDirection="column" gap={3}>
                  {getSettingsByCategory('integrations').map(setting => (
                    <Box key={setting.key}>
                      {renderSettingField(setting)}
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Clear Cache Confirmation Dialog */}
      <Dialog open={clearCacheDialog} onClose={() => setClearCacheDialog(false)}>
        <DialogTitle>Clear System Cache</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to clear the system cache? This action cannot be undone
            and may temporarily affect application performance.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearCacheDialog(false)}>Cancel</Button>
          <Button onClick={handleClearCache} color="error" variant="contained">
            Clear Cache
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SystemSettings;